<template>
  <div id="__Dashboard">
    <div v-if="displayDashboard && renderDashboardControls" class="pa-xs">
      <HeadContents @add-widget="onAddWidget" />
    </div>

    <div v-if="showAlert" class="pa-xs">
      <Alert>
        <div v-html="status.message"></div>
      </Alert>
    </div>

    <CPanel>
      <Announcements />
    </CPanel>

    <div v-if="displayAllAsTabs">
      <!-- Display report filters and dashboard as tabs -->
      <CTabs v-model="tab" :tab-items="tabItems" dense class="pl-xs pr-xs" />

      <CTabPanels v-model="tab">
        <CTabPanel name="dashboard" no-padding>
          <GridLayout :layout="layout" v-if="layout" @layout-updated="onLayoutUpdated" />
        </CTabPanel>

        <CTabPanel name="report-filters" no-padding>
          <CPanel>
            <ReportFilter />
          </CPanel>
        </CTabPanel>
      </CTabPanels>
    </div>
    <div v-else-if="displayDashboard">
      <!-- Display only dashboard -->
      <GridLayout :layout="layout" v-if="layout" @layout-updated="onLayoutUpdated" />
    </div>
    <div v-else-if="displayReportFilters">
      <!-- Display only report filters -->
      <CPanel>
        <ReportFilter />
      </CPanel>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import { isEqual } from 'lodash';

import CTabs from '@/components/uikit/CTabs';
import CTabPanels from '@/components/uikit/CTabs/CTabPanels';
import CTabPanel from '@/components/uikit/CTabs/CTabPanel';
import CPanel from '@/components/uikit/CPanel';
import Alert from '@/components/uikit/Alert';
import Announcements from '@/pages/Dashboard/Announcements';
import Chart from './Chart';

import HeadContents from './HeadContents';
import GridLayout from '@design-system/layouts/GridLayout';
import ReportFilter from './ReportFilter';

const components = {
  chart: Chart,
};

export default {
  name: 'Dashboard',
  components: {
    CTabs,
    CTabPanels,
    CTabPanel,
    CPanel,
    Chart,
    Alert,
    Announcements,
    // ChartWidget,
    GridLayout,
    HeadContents,
    ReportFilter,
  },
  data() {
    return {
      layout: null,
      tab: 'dashboard',
    };
  },
  async created() {
    await this.fetch();
  },
  computed: {
    ...mapState('status', {
      status: state => state.status,
    }),
    ...mapState('user', {
      mspId: state => state.data.msp_id,
    }),
    ...mapGetters('dashboard', {
      storeLayout: 'layout',
      currentDashboard: 'currentDashboard',
    }),

    tabItems() {
      return [
        { label: this.$t('text.dashboard'), value: 'dashboard' },
        { label: this.$t('Report Filters'), value: 'report-filters' },
      ];
    },

    renderDashboardControls() {
      return this.$flag('CEAF5D85-1ABF-4379-A5ED-34286FA5A1BB');
    },

    displayReportFilters() {
      return this.$display('report_filters');
    },

    displayAllAsTabs() {
      return this.displayReportFilters && this.displayDashboard;
    },

    layoutToRender() {
      return this.storeLayout
        ? this.storeLayout
            .filter(item => components[item.widget])
            .map(item => ({
              ...item,
              static: true,
              component: components[item.widget],
            }))
        : [];
    },

    displayDashboard() {
      return this.$display('dashboard_graph');
    },

    showAlert() {
      if (!this.status['message']) return false;
      if (this.status['msp_id'] && Object.keys(this.status['msp_id']).length) {
        return Object.keys(this.status['msp_id'])[0] === this.mspId;
      } else {
        return true;
      }
    },
  },
  watch: {
    layoutToRender(val, oldVal) {
      // This gets called even when nothing actually changed
      // https://codepen.io/pmorch/pen/JQNLdm
      if (isEqual(val, oldVal)) return;
      this.layout = val;
    },
  },
  methods: {
    ...mapActions('dashboard', {
      fetchDashboards: 'fetchDashboards',
      addWidget: 'addWidget',
    }),
    ...mapMutations('dashboard', {
      updateIndex: 'updateIndex',
      updateLayout: 'updateLayout',
    }),
    async fetch() {
      await this.fetchDashboards();
    },
    onAddWidget(widget) {
      this.addWidget(widget);
    },
    onLayoutUpdated(val) {
      this.currentDashboard.layout = val;
    },
  },
};
</script>

<style lang="scss" scoped>
#__Dashboard::v-deep {
  .vue-grid-layout {
    background: #f6f6f6 !important;
    margin: 0 -10px;
  }
}

#__Dashboard {
  padding: $space-md $space-xs;
}
</style>
